import React from "react";
import { toast } from "react-toastify";
import { responseMessage } from "../../../services/platformService";
import { AppDropdown, EmptyState, LoadState, badgeStyle, cardStyle, iconBtnStyle, statusLabel, tdStyle, thStyle } from "../../../components/ui";
import { fmtMoney } from "./glContext";

// Small pieces shared by the general ledger screens.
export const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
export const num = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
export const numTh = { ...thStyle, textAlign: "right" };
export const hint = { fontSize: 13, color: "#6B7280" };

const COLOURS = {
  DRAFT: ["#F3F4F6", "#374151"],
  PENDING_APPROVAL: ["#FEF3C7", "#92400E"],
  APPROVED: ["#DBEAFE", "#1E40AF"],
  POSTED: ["#D1FAE5", "#065F46"],
  REVERSED: ["#F3F4F6", "#4B5563"],
  OPEN: ["#D1FAE5", "#065F46"],
  CLOSED: ["#FEE2E2", "#991B1B"],
  PERMANENTLY_CLOSED: ["#E5E7EB", "#111827"],
  FUTURE: ["#DBEAFE", "#1E40AF"],
  NEVER_OPENED: ["#F3F4F6", "#6B7280"],
  PENDING: ["#FEF3C7", "#92400E"],
  ERROR: ["#FEE2E2", "#991B1B"],
  ACCOUNTED: ["#D1FAE5", "#065F46"],
  BUILT: ["#DBEAFE", "#1E40AF"],
  DOWNLOADED: ["#DBEAFE", "#1E40AF"],
  UPLOADING: ["#FEF3C7", "#92400E"],
  SUBMITTED: ["#FEF3C7", "#92400E"],
  POLLING: ["#FEF3C7", "#92400E"],
  COMPLETED: ["#D1FAE5", "#065F46"],
  MARKED_UPLOADED: ["#D1FAE5", "#065F46"],
  PARTIAL: ["#FEF3C7", "#92400E"],
  FAILED: ["#FEE2E2", "#991B1B"],
  UNCERTAIN: ["#FEE2E2", "#991B1B"],
  NEEDS_REVIEW: ["#FEE2E2", "#991B1B"],
  ACCEPTED: ["#D1FAE5", "#065F46"],
  REJECTED: ["#FEE2E2", "#991B1B"],
  SENT: ["#DBEAFE", "#1E40AF"],
  SUPERSEDED: ["#F3F4F6", "#4B5563"],
  ACTIVE: ["#D1FAE5", "#065F46"],
  INACTIVE: ["#F3F4F6", "#4B5563"],
};
const LABELS = { NEEDS_REVIEW: "Needs review", MARKED_UPLOADED: "Uploaded", PERMANENTLY_CLOSED: "Permanently closed", NEVER_OPENED: "Never opened" };
export function Badge({ status }) {
  const [bg, color] = COLOURS[status] || ["#F3F4F6", "#374151"];
  return <span style={badgeStyle(bg, color)}>{LABELS[status] || statusLabel(status)}</span>;
}

// The ledger every GL screen works in. Hidden when the vendor has a single ledger.
export function LedgerBar({ gl, children }) {
  return (
    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
      {gl.ledgers.length > 1 ? (
        <div style={{ minWidth: 260 }}>
          <AppDropdown id="fin-ledger" value={gl.ledger ? gl.ledger.code : null} options={gl.ledgerOptions} onChange={(e) => gl.chooseLedger(e.value)} placeholder="Ledger" />
        </div>
      ) : gl.ledger ? (
        <div style={{ ...hint, alignSelf: "center" }}>
          Ledger <strong style={{ color: "#111827" }}>{gl.ledger.name}</strong> · {gl.ledger.currency}
        </div>
      ) : null}
      {children}
    </div>
  );
}

// Loading / error / "no ledger yet" around a GL screen.
export function GlState({ gl, children }) {
  return (
    <LoadState loading={gl.loading} error={gl.error} onRetry={gl.reload}>
      {gl.ledger && gl.structure ? children : <EmptyState title="No ledger yet" text="The Finance administrator sets the ledger up under Finance Setup → Ledgers." />}
    </LoadState>
  );
}

// columns: [{ label, render(row), num?, width? }]
export function DataTable({ columns, rows, rowKey = (r) => r.id, onRowClick, minWidth = 640, footer, empty }) {
  if (!rows.length && empty) return empty;
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table className="table mb-0" style={{ minWidth }}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={`${c.label}-${i}`} style={{ ...(c.num ? numTh : thStyle), width: c.width }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} onClick={onRowClick ? () => onRowClick(r) : undefined} style={onRowClick ? { cursor: "pointer" } : undefined}>
              {columns.map((c, j) => (
                <td key={j} style={c.num ? num : { ...tdStyle, ...(c.style || {}) }}>
                  {c.render(r) ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? <tfoot>{footer}</tfoot> : null}
      </table>
    </div>
  );
}

export const money = (n) => (n === null || n === undefined ? "" : Number(n) === 0 ? "" : fmtMoney(n));

// Runs an API call, toasts its message, and reports success.
export async function act(call, okMessage) {
  try {
    const res = await call();
    if (res?.success) {
      toast.success(res.message || okMessage || "Done");
      return res;
    }
    toast.error(responseMessage(res));
  } catch (err) {
    toast.error(err.message || "Something went wrong");
  }
  return null;
}

// Rows → CSV download (reports are exported in the browser from what is on screen).
export function downloadCsv(fileName, header, rows) {
  const cell = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(cell).join(",")).join("\r\n");
  const href = window.URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

export const ACCOUNT_TYPES = [
  { label: "Asset", value: "A" },
  { label: "Liability", value: "L" },
  { label: "Owners' equity", value: "O" },
  { label: "Revenue", value: "R" },
  { label: "Expense", value: "E" },
];
export const accountTypeLabel = (t) => (ACCOUNT_TYPES.find((o) => o.value === t) || {}).label || t || "";
export const QUALIFIER_LABELS = {
  COMPANY: "Company",
  BU: "Business unit",
  COST_CENTER: "Cost centre",
  PROFIT_CENTER: "Profit centre",
  ACCOUNT: "Account",
  SUBACCOUNT: "Sub-account",
  PROJECT: "Project",
  PRODUCT: "Product",
  INTERCOMPANY: "Intercompany",
  FUTURE: "Future",
};
